/* =====================================================
   Blackout dates for delivery and pickup
   Shipping blackouts handled server-side by the API
   ===================================================== */
const blackoutDates = {
  delivery: [
    // Christmas weekend 2025
    '2025-12-20', '2025-12-21', '2025-12-22',
    '2025-12-27', '2025-12-28', '2025-12-29',
    // Add more delivery blackout dates here as needed
  ],
  pickup: [
    // Christmas 2025
    '2025-12-20', '2025-12-21', '2025-12-22',
    '2025-12-24', '2025-12-25',
    // New Year 2026
    '2026-01-01',
    // Thanksgiving 2025
    '2025-11-27',
    // Add more pickup blackout dates here as needed
  ]
};

/* =====================================================
   Helpers
   ===================================================== */

function getArizonaTime() {
  const now = new Date();
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  return new Date(utc - (3600000 * 7)); // MST is UTC-7, no DST
}

function formatDateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return year + '-' + month + '-' + day;
}

function isBlackoutDate(date, serviceType) {
  const dateString = formatDateString(date);
  return blackoutDates[serviceType] && blackoutDates[serviceType].includes(dateString);
}

function formatDayLabel(date, referenceDate) {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = days[date.getDay()];
  const msPerDay = 86400000;
  const refMidnight = new Date(referenceDate);
  refMidnight.setHours(0, 0, 0, 0);
  const dateMidnight = new Date(date);
  dateMidnight.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((dateMidnight - refMidnight) / msPerDay);

  if (dayDiff > 7) {
    return dayName + ' ' + (date.getMonth() + 1) + '/' + date.getDate();
  }
  return dayName;
}

/**
 * Next delivery day (Sat/Sun/Mon).
 * Cutoff: 4pm AZ the day before. Next-day only, no same-day.
 *
 * Mon-Thu (any time) -> Saturday
 * Fri before 4pm     -> Saturday
 * Fri after 4pm      -> Sunday
 * Sat before 4pm     -> Sunday
 * Sat after 4pm      -> Monday
 * Sun before 4pm     -> Monday
 * Sun after 4pm      -> Saturday
 */
function getNextDeliveryDay(azTime) {
  const day = azTime.getDay();
  const hour = azTime.getHours();
  let daysUntil;

  if (day >= 1 && day <= 4) daysUntil = 6 - day;          // Mon-Thu -> Sat
  else if (day === 5) daysUntil = hour < 16 ? 1 : 2;       // Fri -> Sat or Sun
  else if (day === 6) daysUntil = hour < 16 ? 1 : 2;       // Sat -> Sun or Mon
  else daysUntil = hour < 16 ? 1 : 6;                       // Sun -> Mon or next Sat

  const candidate = new Date(azTime);
  candidate.setHours(0, 0, 0, 0);
  candidate.setDate(candidate.getDate() + daysUntil);

  // Skip blackout dates, advancing through Sat->Sun->Mon->Sat cycle
  let safety = 0;
  while (isBlackoutDate(candidate, 'delivery') && safety < 30) {
    candidate.setDate(candidate.getDate() + 1);
    const d = candidate.getDay();
    // If we landed on Tue-Fri, jump to Saturday
    if (d >= 2 && d <= 5) {
      candidate.setDate(candidate.getDate() + (6 - d));
    }
    safety++;
  }

  return candidate;
}

/**
 * Pickup day label.
 * Pickup is daily (7 days/week). Cutoff: 4:15pm AZ.
 * Before 4:15pm and not blackout -> "Today"
 * Otherwise -> "Tomorrow" or next non-blackout day name
 */
function getPickupDay(azTime) {
  const hour = azTime.getHours();
  const minute = azTime.getMinutes();
  const isBefore415 = hour < 16 || (hour === 16 && minute < 15);

  if (isBefore415 && !isBlackoutDate(azTime, 'pickup')) {
    return 'Today';
  }

  const tomorrow = new Date(azTime);
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const candidate = new Date(tomorrow);
  let safety = 0;
  while (isBlackoutDate(candidate, 'pickup') && safety < 30) {
    candidate.setDate(candidate.getDate() + 1);
    safety++;
  }

  if (candidate.getTime() === tomorrow.getTime()) return 'Tomorrow';
  return formatDayLabel(candidate, azTime);
}

/* =====================================================
   API
   ===================================================== */

const BH_ZIP_API = 'https://bh-zipcheck-ngny3uddpa-uc.a.run.app';

function fetchShippingStatus(zip) {
  return fetch(BH_ZIP_API + '?zip=' + zip)
    .then(function(response) {
      if (!response.ok) throw new Error('API request failed');
      return response.json();
    });
}

function fetchZipFromIP() {
  return fetch(BH_ZIP_API + '/ip-zip')
    .then(function(response) {
      if (!response.ok) throw new Error('IP lookup failed');
      return response.json();
    })
    .then(function(data) {
      if (data.zip && /^\d{5}$/.test(data.zip)) {
        return data.zip;
      }
      throw new Error('No valid zip in response');
    });
}

/* =====================================================
   East Coast arrival promise

   zipcheck returns min_delivery_date, max_delivery_date,
   cutoff_matters and cutoff_at only for east coast zips on a
   service phase that has reached them. West coast zips, and
   east coast zips before the phase dial moves, come back
   without these fields — every function below then yields
   nothing and the existing copy renders untouched.
   ===================================================== */

const BH_EC_FULL_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const BH_EC_SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const BH_EC_SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Target window for a zip we can't serve yet (zipcheck planned_launch, e.g.
// the Texas facility footprint -> 'spring_2027'). Stored only while the zip
// is unserviceable; copy falls back to no date without it.
function bhStorePlannedLaunch(result) {
  if (result && !result.serviceable && result.planned_launch) {
    localStorage.setItem('bhZipPlanned', result.planned_launch);
  } else {
    localStorage.removeItem('bhZipPlanned');
  }
}

function bhIsPlannedSpring2027() {
  return localStorage.getItem('bhZipPlanned') === 'spring_2027';
}

var BH_SPRING_2027_LINE = "We're aiming to reach your ZIP code in spring 2027.";

function bhClearEcPromise() {
  localStorage.removeItem('bhEcMinDelivery');
  localStorage.removeItem('bhEcMaxDelivery');
  localStorage.removeItem('bhEcCutoffMatters');
  localStorage.removeItem('bhEcCutoffAt');
}

function bhStoreEcPromise(result) {
  if (!result || !result.serviceable || !result.min_delivery_date) {
    bhClearEcPromise();
    return;
  }
  localStorage.setItem('bhEcMinDelivery', result.min_delivery_date);
  localStorage.setItem('bhEcMaxDelivery', result.max_delivery_date || result.min_delivery_date);
  localStorage.setItem('bhEcCutoffMatters', result.cutoff_matters ? 'true' : 'false');
  localStorage.setItem('bhEcCutoffAt', result.cutoff_at || '');
}

function bhGetEcPromise() {
  var min = localStorage.getItem('bhEcMinDelivery');
  if (!min) return null;
  return {
    min: min,
    max: localStorage.getItem('bhEcMaxDelivery') || min,
    cutoffMatters: localStorage.getItem('bhEcCutoffMatters') === 'true',
    cutoffAt: localStorage.getItem('bhEcCutoffAt') || ''
  };
}

// "2026-08-05" at local midnight. new Date(iso) would read it as UTC and
// render the day before anywhere west of Greenwich.
function bhParseEcDate(iso) {
  var parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').trim());
  if (!parts) return null;
  var parsed = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  return isNaN(parsed.getTime()) ? null : parsed;
}

// Relative weekday names are only honest inside the current week, so they
// apply to arrivals landing before the coming Sunday.
function bhEcIsThisWeek(date, now) {
  var comingSunday = new Date(now);
  comingSunday.setHours(0, 0, 0, 0);
  comingSunday.setDate(comingSunday.getDate() + (7 - comingSunday.getDay()));
  return date.getTime() < comingSunday.getTime();
}

// Abbreviated weekday, no comma: the product page and the widget are both
// space constrained.
function bhEcShortDate(date) {
  return BH_EC_SHORT_DAYS[date.getDay()] + ' ' + BH_EC_SHORT_MONTHS[date.getMonth()] + ' ' + date.getDate();
}

// Two dates joined by "or", collapsing to one when they match. Never a date
// range dash, never a ship date, never a price.
function bhEcArrivalSentence(min, max, now) {
  var collapsed = min.getTime() === max.getTime();

  if (bhEcIsThisWeek(min, now) && bhEcIsThisWeek(max, now)) {
    if (collapsed) return 'Arrives this ' + BH_EC_FULL_DAYS[min.getDay()];
    return 'Arrives this ' + BH_EC_FULL_DAYS[min.getDay()] + ' or ' + BH_EC_FULL_DAYS[max.getDay()];
  }

  if (collapsed) return 'Arrives ' + bhEcShortDate(min);
  return 'Arrives ' + bhEcShortDate(min) + ' or ' + bhEcShortDate(max);
}

// Milliseconds left on the cutoff, or null when the countdown must not show.
// cutoff_matters is the server's answer to whether beating the cutoff actually
// moves the arrival date; a countdown that changes nothing is a claim the
// customer can check and catch us on.
function bhEcCutoffRemaining(promise, now) {
  if (!promise.cutoffMatters || !promise.cutoffAt) return null;
  var target = new Date(promise.cutoffAt).getTime();
  if (isNaN(target)) return null;
  var remaining = target - now.getTime();
  return remaining > 0 ? remaining : null;
}

function bhEcFormatRemaining(ms) {
  var totalMinutes = Math.floor(ms / 60000);
  var hours = Math.floor(totalMinutes / 60);
  var minutes = totalMinutes % 60;

  if (hours > 0) {
    var hoursText = hours + (hours === 1 ? ' hour' : ' hours');
    if (minutes === 0) return hoursText;
    return hoursText + ' ' + minutes + (minutes === 1 ? ' minute' : ' minutes');
  }
  if (minutes > 0) return minutes + (minutes === 1 ? ' minute' : ' minutes');
  return 'few seconds';
}

function bhEcCutoffClause(remaining) {
  return ' when you order in the next ' + bhEcFormatRemaining(remaining);
}

// The shared arrival block for the product page and the zip widget. Returns
// null when there is no east coast promise to show.
function bhEcMessageHtml() {
  var promise = bhGetEcPromise();
  if (!promise) return null;

  var min = bhParseEcDate(promise.min);
  var max = bhParseEcDate(promise.max) || min;
  if (!min) return null;

  var now = new Date();
  var html = '<span class="bh-ec-arrival">' + bhEcArrivalSentence(min, max, now);

  var remaining = bhEcCutoffRemaining(promise, now);
  if (remaining !== null) {
    html += '<span class="bh-ec-cutoff" data-bh-cutoff-at="' + promise.cutoffAt + '">' +
      bhEcCutoffClause(remaining) + '</span>';
  }
  html += '.</span>';

  var snowflake = window.bhSnowflakeUrl
    ? '<img src="' + window.bhSnowflakeUrl + '" alt="" class="bh-shipping-snowflake">'
    : '';
  html += '<span class="bh-ec-dry-ice">' + snowflake + 'Ships frozen on dry ice.</span>';

  return html;
}

// Re-read the promise once the cutoff passes: the dates have rolled forward.
// One request per expiry: every countdown on the page expires in the same
// second, and a background tab replays all of its pending ticks at once.
var bhEcRefreshInFlight = null;

function bhRefreshEcPromise() {
  var savedZip = localStorage.getItem('bhZipCode');
  if (!savedZip) return null;
  if (bhEcRefreshInFlight) return bhEcRefreshInFlight;

  bhEcRefreshInFlight = fetchShippingStatus(savedZip)
    .then(function(result) {
      if (result.shipping_estimate) {
        localStorage.setItem('bhShippingEstimate', result.shipping_estimate);
      }
      bhStoreEcPromise(result);
      updateDeliveryInfo();
      updatePostalChecker();
      updateAddToCartButtons();
    })
    .catch(function() { /* keep the dates already on screen, minus the countdown */ })
    .finally(function() { bhEcRefreshInFlight = null; });
  return bhEcRefreshInFlight;
}

// Clear the countdowns inside a container before its innerHTML is replaced.
// A span that is detached with its interval still running keeps ticking
// against the orphaned node, and every orphan used to fire its own refresh.
function bhStopEcCountdowns(root) {
  if (!root) return;
  root.querySelectorAll('.bh-ec-cutoff').forEach(function(el) {
    if (el._bhCutoffInterval) {
      clearInterval(el._bhCutoffInterval);
      el._bhCutoffInterval = null;
    }
  });
}

function bhStartEcCountdowns(root) {
  var countdowns = (root || document).querySelectorAll('.bh-ec-cutoff[data-bh-cutoff-at]');

  countdowns.forEach(function(el) {
    if (el._bhCutoffInterval) clearInterval(el._bhCutoffInterval);

    var target = new Date(el.getAttribute('data-bh-cutoff-at')).getTime();
    if (isNaN(target)) return;

    function stop() {
      if (el._bhCutoffInterval) clearInterval(el._bhCutoffInterval);
      el._bhCutoffInterval = null;
    }

    function tick() {
      // A span that a re-render replaced must never reach zipcheck
      if (!el.isConnected) {
        stop();
        return false;
      }

      var remaining = target - Date.now();

      if (remaining <= 0) {
        stop();
        el.textContent = '';
        bhRefreshEcPromise();
        return false;
      }

      var clause = bhEcCutoffClause(remaining);
      if (el.textContent !== clause) el.textContent = clause;
      return true;
    }

    // Render immediately; an already-expired span has nothing left to count
    if (!tick()) return;
    el._bhCutoffInterval = setInterval(tick, 1000);
  });
}

/* =====================================================
   Core functions
   ===================================================== */

function updateDeliveryHeading(savedZip, zipType) {
  var headingZips = document.querySelectorAll('.bh-delivery-heading-zip');
  headingZips.forEach(function(el) {
    if (savedZip && zipType) {
      el.classList.add('visible');
      var link = el.querySelector('.bh-zip-link');
      if (link) link.textContent = savedZip;
    } else {
      el.classList.remove('visible');
    }
  });
}

function checkPostal(zipcode) {
  const cleanZip = zipcode.trim();

  if (!cleanZip || cleanZip.length !== 5 || !/^\d{5}$/.test(cleanZip)) {
    return;
  }

  // Whitelisted zips -> delivery (synchronous, no API call)
  if (window.bhZipCodes && window.bhZipCodes.whitelisted.includes(cleanZip)) {
    localStorage.setItem('bhZipCode', cleanZip);
    localStorage.setItem('bhZipType', 'delivery');
    localStorage.removeItem('bhShippingEstimate');
    localStorage.removeItem('bhZipPlanned');
    bhClearEcPromise();
    updatePostalChecker();
    updateAddToCartButtons();
    updateHomepageCountdown();
    updateRegionScopedCountdowns();
    updateEcFaqBlocks();
    updateDeliveryInfo();
    if (typeof updatePromoHeader === 'function') updatePromoHeader();
    updateAzOnlyCatalogVisibility();

    // Non-blocking: fetch shipping estimate to populate the shipping card
    fetchShippingStatus(cleanZip)
      .then(function(result) {
        if (result.shipping_estimate) {
          localStorage.setItem('bhShippingEstimate', result.shipping_estimate);
          updateShippingCard(result.shipping_estimate);
        }
      })
      .catch(function() { /* shipping card stays as "..." */ });

    return;
  }

  // Non-whitelisted -> check API for shipping eligibility
  const submitButtons = document.querySelectorAll('.postal-submit-btn');
  submitButtons.forEach(function(btn) { btn.disabled = true; btn.textContent = '...'; });

  fetchShippingStatus(cleanZip)
    .then(function(result) {
      bhStorePlannedLaunch(result);
      if (result.serviceable) {
        localStorage.setItem('bhZipCode', cleanZip);
        localStorage.setItem('bhZipType', 'shipping');
        localStorage.setItem('bhShippingEstimate', result.shipping_estimate || '');
        localStorage.removeItem('bhZipPhase');
        bhStoreEcPromise(result);
      } else if (
        result.zip_phase != null &&
        result.zip_phase > result.current_phase &&
        window.bhLaunchDates && window.bhLaunchDates[result.zip_phase]
      ) {
        localStorage.setItem('bhZipCode', cleanZip);
        localStorage.setItem('bhZipType', 'upcoming');
        localStorage.setItem('bhZipPhase', result.zip_phase);
        localStorage.removeItem('bhShippingEstimate');
        bhClearEcPromise();
      } else {
        localStorage.setItem('bhZipCode', cleanZip);
        localStorage.setItem('bhZipType', 'none');
        localStorage.removeItem('bhShippingEstimate');
        localStorage.removeItem('bhZipPhase');
        bhClearEcPromise();
      }
    })
    .catch(function() {
      // API failure -> treat as not serviceable
      localStorage.setItem('bhZipCode', cleanZip);
      localStorage.setItem('bhZipType', 'none');
      localStorage.removeItem('bhShippingEstimate');
      localStorage.removeItem('bhZipPlanned');
      bhClearEcPromise();
    })
    .finally(function() {
      submitButtons.forEach(function(btn) { btn.disabled = false; btn.textContent = 'CHECK'; });
      updatePostalChecker();
      updateAddToCartButtons();
      updateHomepageCountdown();
      updateRegionScopedCountdowns();
      updateEcFaqBlocks();
      updateDeliveryInfo();
      if (typeof updatePromoHeader === 'function') updatePromoHeader();
      updateAzOnlyCatalogVisibility();
    });
}

function updatePostalChecker() {
  const savedZip = localStorage.getItem('bhZipCode');
  const zipType = localStorage.getItem('bhZipType');
  const postalCheckerForms = document.querySelectorAll('.postal-checker-form');
  const postalCheckerChecked = document.querySelectorAll('.postal-checker-checked');
  const checkoutButtons = document.querySelectorAll('.bh-checkout-button');

  var cartDiscountBar = document.getElementById('cart-discount-bar');
  var setCheckoutBlocked = function(isBlocked) {
    if (typeof setCheckoutPostalBlocked === 'function') {
      setCheckoutPostalBlocked(isBlocked);
      return;
    }

    checkoutButtons.forEach(function(btn) {
      btn.classList.toggle('disabled', isBlocked);
      btn.setAttribute('aria-disabled', isBlocked ? 'true' : 'false');
    });
  };

  // Cart drawer: once a zip is known, the verdict replaces the
  // "Check if we deliver to your area" question header and the message body
  // carries only the follow-up line.
  var drawerHeading = document.querySelector('#cart-zip-check > span');

  if (savedZip && zipType) {
    postalCheckerForms.forEach(function(form) { form.classList.add('hidden'); });

    postalCheckerChecked.forEach(function(element) {
      element.classList.remove('hidden');
      const message = element.querySelector('.checked-message');
      var inDrawer = !!element.closest('#cart-zip-check');
      bhStopEcCountdowns(message);
      if (zipType === 'delivery') {
        var deliverVerdict = "We deliver to " + savedZip + ". You're in.";
        if (inDrawer && drawerHeading) {
          drawerHeading.textContent = deliverVerdict;
          message.innerHTML = "";
        } else {
          message.innerHTML = deliverVerdict;
        }
        setCheckoutBlocked(false);
        if (cartDiscountBar) {
          if (window._cartHasDiscountEligible) {
            cartDiscountBar.classList.remove('hidden');
          } else {
            cartDiscountBar.classList.add('hidden');
          }
        }
        if (cartDiscountBar) cartDiscountBar.classList.remove('hidden');
      } else if (zipType === 'shipping') {
        // Cart ZIP status stays a simple eligibility confirmation in every region.
        // Product-page and standalone ZIP-widget delivery promises remain separate.
        var ecHtml = inDrawer ? null : bhEcMessageHtml();
        var shipVerdict = "We ship to " + savedZip + ". You're in.";
        if (inDrawer && drawerHeading) {
          drawerHeading.textContent = shipVerdict;
          message.innerHTML = ecHtml || '';
        } else {
          message.innerHTML = shipVerdict + (ecHtml || '');
        }
        if (ecHtml) bhStartEcCountdowns(message);
        setCheckoutBlocked(false);
        if (cartDiscountBar) cartDiscountBar.classList.add('hidden');
      } else if (zipType === 'upcoming') {
        if (inDrawer && drawerHeading) drawerHeading.textContent = "Check if we deliver to your area";
        message.innerHTML = "";
        setCheckoutBlocked(true);
        if (cartDiscountBar) cartDiscountBar.classList.add('hidden');
      } else {
        // In the cart drawer a mixed cart gets merch-aware copy: burritos are
        // what's blocked, and removing them is the way through checkout.
        var merchMix = (typeof bhCartMerchMix === 'function') ? bhCartMerchMix() : null;
        var merchAware = merchMix && merchMix.hasMerch && !merchMix.merchOnly && inDrawer;
        var blockedVerdict = merchAware
          ? "We don't deliver burritos to " + savedZip + "...yet."
          : "We don't deliver to " + savedZip + "...yet.";
        var blockedFollowup = bhIsPlannedSpring2027()
          ? (merchAware
            ? BH_SPRING_2027_LINE + " Remove burritos from your cart to check out with your merch."
            : BH_SPRING_2027_LINE + " Join our <a style='text-decoration: underline;' href='" + window.emailListUrl + "'>email list</a> to hear when we can ship to you.")
          : (merchAware
            ? "Remove burritos from your cart to check out with your merch."
            : "Join our <a style='text-decoration: underline;' href='" + window.emailListUrl + "'>email list</a>, and we'll keep you posted when we start shipping.");
        if (inDrawer && drawerHeading) {
          drawerHeading.textContent = blockedVerdict;
          message.innerHTML = blockedFollowup;
        } else {
          message.innerHTML = blockedVerdict + "<br> " + blockedFollowup;
        }
        setCheckoutBlocked(true);
        if (cartDiscountBar) cartDiscountBar.classList.add('hidden');
      }
    });
  } else {
    if (drawerHeading) drawerHeading.textContent = "Check if we deliver to your area";
    postalCheckerChecked.forEach(function(element) {
      element.classList.add('hidden');
      bhStopEcCountdowns(element);
    });
    setCheckoutBlocked(true);
    if (cartDiscountBar) cartDiscountBar.classList.add('hidden');
  }

  // Re-check az-only cart restriction when zip changes
  if (typeof checkAzOnlyRestriction === 'function') checkAzOnlyRestriction();

  // T9: the side cart's flat-shipping summary keys off the same saved-zip state
  if (typeof updateEcShippingSummary === 'function') updateEcShippingSummary();

  // Tier-scoped blocks (inventory bars, details) follow the same zip state
  updateTierVisibility();
  updateSoldOutCountdowns();
  updateRegionQuantityOptions();
  updateRegionalFlavorVisibility();
}

function updateRegionalFlavorVisibility() {
  var savedZip = localStorage.getItem('bhZipCode');
  var zipType = localStorage.getItem('bhZipType');
  var region = null;
  if (/^\d{5}$/.test(savedZip || '')) {
    if (zipType === 'delivery') region = 'local';
    else if (zipType === 'shipping') region = bhZipIsEastCoast() ? 'east' : 'west';
  }
  document.querySelectorAll('[data-bh-flavor-inventory]').forEach(function(flavor) {
    var raw = region ? flavor.getAttribute('data-bh-flavor-' + region) : null;
    var packs = raw === null || raw.trim() === '' ? null : Number(raw);
    flavor.hidden = packs !== null && Number.isFinite(packs) && packs <= 0;
  });
}

function updateAzOnlyCatalogVisibility() {
  var savedZip = localStorage.getItem('bhZipCode');
  var zipType = localStorage.getItem('bhZipType');
  var isNonAzShipping =
    zipType === 'shipping' &&
    savedZip &&
    savedZip.substring(0, 2) !== '85' &&
    savedZip.substring(0, 2) !== '86';

  // Phoenix-only products can only be bought on the local delivery tier, so
  // hide them for any saved zip outside it (shipping/upcoming/unserviceable).
  // Visitors with no saved zip keep seeing them (we can't know their location).
  var isNonDeliveryZip = !!savedZip && !!zipType && zipType !== 'delivery';

  // Western-only products ship from Azoria only, so hide them from east coast
  // zips (live Grip zips and upcoming east waves). Western zips that can't
  // ship yet keep seeing them — their wave will carry the product.
  var isEastCoast = !!savedZip && !!zipType && bhZipIsEastCoast();

  document.body.classList.toggle('bh-hide-az-only', isNonAzShipping);
  document.body.classList.toggle('bh-hide-phoenix-only', isNonDeliveryZip);
  document.body.classList.toggle('bh-hide-western-only', isEastCoast);

  // Keep pagination dots aligned with visible cards: dots are server-rendered
  // by index, so hide the dot whose index matches each hidden catalog item.
  document.querySelectorAll('.bh-catalog-list-wrapper').forEach(function(wrapper) {
    var items = wrapper.querySelectorAll('[data-catalog-item]');
    var dotsWrapper = wrapper.parentElement && wrapper.parentElement.querySelector('.bh-pagination-wrapper');
    if (!dotsWrapper) return;
    var dots = dotsWrapper.querySelectorAll('.pagination-dot-wrapper');
    items.forEach(function(item, idx) {
      // bh-az-hidden is a generic "hidden dot" marker, reused for all rules
      var hide = (isNonAzShipping && item.hasAttribute('data-az-only')) ||
                 (isNonDeliveryZip && item.hasAttribute('data-phoenix-only')) ||
                 (isEastCoast && item.hasAttribute('data-western-only'));
      if (dots[idx]) dots[idx].classList.toggle('bh-az-hidden', hide);
    });
  });
}

/* =====================================================
   Split-inventory drops

   bh-buy-buttons emits window.bhDropInventory (pack counts)
   when the product carries the total_inventory_packs
   metafield. Locals sell against the non-shippable pool,
   everyone else against the shippable pool.
   ===================================================== */

// Packs remaining for the visitor's audience, or null when the current
// product isn't a split-inventory drop.
function bhAudiencePacksRemaining() {
  if (!window.bhDropInventory) return null;
  var inv = window.bhDropInventory;
  var zipType = localStorage.getItem('bhZipType');
  return zipType === 'delivery'
    ? Math.max(inv.total - inv.shippable, 0)
    : inv.shippable;
}

function bhButtonPacksRemaining() {
  var inv = window.bhDropInventory;
  var savedZip = localStorage.getItem('bhZipCode');
  if (inv && Number.isFinite(inv.west) && /^\d{5}$/.test(savedZip || '') &&
      localStorage.getItem('bhZipType') === 'shipping' && !bhZipIsEastCoast()) {
    return inv.west;
  }
  return bhAudiencePacksRemaining();
}

// Countdown blocks with "Only show when sold out" render hidden and are
// revealed only when the visitor's own pool is empty.
function updateSoldOutCountdowns() {
  var els = document.querySelectorAll('[data-bh-sold-out-only]');
  if (!els.length) return;
  var packs = bhAudiencePacksRemaining();
  var show = packs !== null && packs <= 0;
  els.forEach(function(el) { el.classList.toggle('hidden', !show); });
}

function updateAddToCartButtons() {
  var zipType = localStorage.getItem('bhZipType');
  var buttons = document.querySelectorAll('.cartButton');
  buttons.forEach(function(btn) {
    var overrideHtml = null;

    if (zipType === 'upcoming') {
      overrideHtml = '<span class="cta">Coming Soon</span>';
    } else if (window.bhDropInventory && btn.closest('product-form')) {
      // Split-inventory drops (bh-buy-buttons emits bhDropInventory when the
      // total_inventory_packs metafield exists). Scoped to the product form
      // so app/upsell buttons are untouched.
      var packs = bhButtonPacksRemaining();
      if (packs !== null && packs <= 0) {
        // A future scheduled drop means this empty pool is between batches, not
        // gone for good: match the server-rendered "Coming Soon" button.
        var comingSoon = window.bhNextDropTs && window.bhNextDropTs > Date.now();
        var label = (comingSoon && btn.getAttribute('data-between-drops-label')) || btn.getAttribute('data-sold-out-label') || (comingSoon
          ? 'Coming Soon'
          : ((window.variantStrings && window.variantStrings.soldOut) || 'Sold Out'));
        overrideHtml = '<span class="cta">' + label + '</span>';
      }
    }

    if (overrideHtml) {
      // Only save the original once; a repeat run while already overridden
      // would clobber the saved markup and break restore
      if (!btn.hasAttribute('data-original-html')) {
        btn.setAttribute('data-original-html', btn.innerHTML);
        btn.setAttribute('data-original-disabled', btn.disabled ? 'true' : 'false');
      }
      btn.disabled = true;
      btn.innerHTML = overrideHtml;
    } else if (btn.hasAttribute('data-original-html')) {
      btn.innerHTML = btn.getAttribute('data-original-html');
      btn.disabled = btn.getAttribute('data-original-disabled') === 'true';
      btn.removeAttribute('data-original-html');
      btn.removeAttribute('data-original-disabled');
    }
  });
}

/* =====================================================
   Zip tier visibility

   Blocks carrying data-visible-tiers (details blocks, inventory
   bars) are settled at first paint by the inline script in
   bh-product-description; this re-evaluates them when the saved
   zip changes without a reload, both hiding and showing.
   ===================================================== */

function updateTierVisibility() {
  var zipType = localStorage.getItem('bhZipType');
  var tier = (zipType === 'delivery' || zipType === 'shipping' || zipType === 'upcoming') ? zipType : 'default';
  document.querySelectorAll('[data-visible-tiers]').forEach(function(el) {
    var tiers = el.getAttribute('data-visible-tiers');
    var region = el.getAttribute('data-bh-inventory-region');
    if ((region === 'east' && (tier !== 'shipping' || !bhZipIsEastCoast())) ||
        (region === 'west' && (tier !== 'shipping' || bhZipIsEastCoast())) ||
        (region === 'local' && tier !== 'delivery')) {
      el.style.display = 'none';
      return;
    }
    if (!tiers || tiers === 'all') {
      el.style.display = '';
      return;
    }
    el.style.display = tiers.indexOf(tier) === -1 ? 'none' : '';
  });
}

/* =====================================================
   Region-specific quantity options

   Quantity blocks with an east coast option set render the
   union of both sets; east-only buttons start hidden and the
   snippet's inline script settles first paint. This keeps
   them in sync when the saved zip changes without a reload,
   and resets a selection that just got hidden so the form
   never carries an invisible quantity.
   ===================================================== */

function updateRegionQuantityOptions() {
  var selectors = document.querySelectorAll('quantity-selector');
  if (!selectors.length) return;
  var savedZip = localStorage.getItem('bhZipCode');
  var zipType = localStorage.getItem('bhZipType');
  var isEast = !!savedZip && !!zipType && bhZipIsEastCoast();
  selectors.forEach(function(sel) {
    var eastEls = sel.querySelectorAll('[data-bh-east-only]');
    var westEls = sel.querySelectorAll('[data-bh-west-only]');
    if (!eastEls.length && !westEls.length) return;
    eastEls.forEach(function(el) {
      el.hidden = !isEast;
      if (el.tagName === 'OPTION') el.disabled = !isEast;
    });
    westEls.forEach(function(el) {
      el.hidden = isEast;
      if (el.tagName === 'OPTION') el.disabled = isEast;
    });

    var defaultQuantity = sel.getAttribute('data-default-quantity');
    var active = sel.querySelector('.quantity-btn.active');
    if (active && active.hidden) {
      var defBtn = sel.querySelector('.quantity-btn[data-quantity="' + defaultQuantity + '"]');
      // click() routes through QuantitySelector's handler, which fixes the
      // active class, the form's quantity input, and the dynamic price
      if (defBtn && !defBtn.hidden) defBtn.click();
    }
    var select = sel.querySelector('.quantity-select');
    if (select) {
      var selectedOpt = select.options[select.selectedIndex];
      if (selectedOpt && selectedOpt.disabled) {
        select.value = defaultQuantity;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });
}

function updateHomepageCountdown() {
  var zipType = localStorage.getItem('bhZipType');
  var allCountdowns = document.querySelectorAll('.bh-launch-countdown');
  allCountdowns.forEach(function(el) {
    if (el.closest('.bh-delivery-info')) return; // skip product page ones
    if (zipType === 'upcoming') {
      el.classList.remove('hidden');
      startLaunchCountdown(el);
    } else {
      el.classList.add('hidden');
    }
  });
}

/* =====================================================
   Region scoped product drop countdowns

   A drop that does not ship east should not count down for an
   east coast visitor: the countdown promises something they
   cannot buy. Merchants pick the scope per drop on the
   countdown section; visitors with no saved zip keep the
   countdown, since we cannot know where they are.
   ===================================================== */

// True when the saved zip belongs to an east coast wave, live or upcoming.
// Live east coast zips carry the Grip promise fields (zipcheck returns
// min_delivery_date only for phases 7 and up); upcoming ones carry the phase.
function bhZipIsEastCoast() {
  if (localStorage.getItem('bhEcMinDelivery')) return true;
  var phase = parseInt(localStorage.getItem('bhZipPhase'), 10);
  return !isNaN(phase) && phase >= (window.bhEcMinPhase || 7);
}

function updateRegionScopedCountdowns() {
  var isEast = bhZipIsEastCoast();
  document.querySelectorAll('[data-bh-region-scope="west_coast"]').forEach(function(scoped) {
    if (isEast && !scoped._bhRegionSwapped) {
      var note = scoped.getAttribute('data-bh-region-message');
      scoped.innerHTML = note ? '<p class="bh-region-note"></p>' : '';
      var noteEl = scoped.querySelector('.bh-region-note');
      if (noteEl) noteEl.textContent = note;
      scoped._bhRegionSwapped = true;
    }
    scoped.classList.remove('bh-region-pending');
  });
}

/* =====================================================
   East coast FAQ variants

   FAQ blocks marked in the bh-faq section schema swap to east
   coast copy or hide entirely, but only for visitors whose
   saved zip has live east coast shipping (the Grip promise
   fields are present — zipcheck returns them only for phases
   the dial has reached). Everyone else, and every east coast
   zip before its wave opens, keeps today's copy untouched.
   Reversible on purpose: re-checking a different zip in the
   same session settles the FAQ back.
   ===================================================== */

function updateEcFaqBlocks() {
  var isLiveEast = !!localStorage.getItem('bhEcMinDelivery');
  document.querySelectorAll('[data-bh-ec-faq]').forEach(function(item) {
    if (item.getAttribute('data-bh-ec-faq') === 'hide') {
      item.classList.toggle('hidden', isLiveEast);
      return;
    }
    item.querySelectorAll('.bh-ec-faq-default').forEach(function(el) {
      el.classList.toggle('hidden', isLiveEast);
    });
    item.querySelectorAll('.bh-ec-faq-ec').forEach(function(el) {
      el.classList.toggle('hidden', !isLiveEast);
    });
  });
}

function updateShippingCard(estimate) {
  var shipDay = '...';
  if (estimate) {
    var match = estimate.match(/Ships\s+(\w+)/);
    if (match) shipDay = match[1];
  }
  var els = document.querySelectorAll('#bh-ships-by-day');
  els.forEach(function(el) {
    // Phoenix-only products never ship; keep their "Unavailable" label
    if (el.closest('.bh-delivery-info[data-phoenix-only="true"]')) return;
    // Drops on a fixed shipping schedule render their day in Liquid
    if (el.closest('.bh-delivery-info[data-ship-day-override]')) return;
    el.textContent = shipDay;
  });
}

function updateDeliveryInfo() {
  const savedZip = localStorage.getItem('bhZipCode');
  const zipType = localStorage.getItem('bhZipType');
  const deliveryInfo = document.querySelectorAll('.bh-delivery-info');

  updateDeliveryHeading(savedZip, zipType);

  // Show/hide local discount message
  var discountMsgs = document.querySelectorAll('.bh-local-discount-msg');
  discountMsgs.forEach(function(el) {
    if (zipType === 'delivery') {
      el.classList.remove('hidden');
    } else {
      el.classList.add('hidden');
    }
  });

  if (!savedZip || !zipType) {
    deliveryInfo.forEach(function(el) {
      el.classList.add('info-hidden');
      bhStopEcCountdowns(el);
    });
    return;
  }

  deliveryInfo.forEach(function(element) {
    element.classList.remove('info-hidden');
    const message = element.querySelector('.delivery-message');
    bhStopEcCountdowns(message);
    const cards = element.querySelector('.bh-delivery-cards');
    const waitlist = element.querySelector('.get-on-waitlist');

    // Hide countdown by default — only the upcoming branch shows it
    var countdownEl = element.querySelector('.bh-launch-countdown');
    if (countdownEl) countdownEl.classList.add('hidden');

    // Reset description-box alignment (upcoming state centers it)
    var descBox = element.closest('.description-box');
    if (descBox) descBox.style.textAlign = '';

    if (zipType === 'delivery') {
      const azTime = getArizonaTime();
      const deliveryDay = formatDayLabel(getNextDeliveryDay(azTime), azTime);
      const pickupDay = getPickupDay(azTime);

      // Hide single-line message, show cards
      message.style.display = 'none';
      if (cards) cards.classList.add('visible');
      waitlist.classList.add('hidden');

      // Populate delivery and pickup cards immediately
      var deliveryDayEls = element.querySelectorAll('#bh-delivery-day');
      deliveryDayEls.forEach(function(el) { el.textContent = deliveryDay; });
      var pickupDayEls = element.querySelectorAll('#bh-pickup-day');
      pickupDayEls.forEach(function(el) { el.textContent = pickupDay; });

      // Shipping card shows "..." until API responds — but Phoenix-only
      // products never ship, so their shipping card reads "Unavailable"
      var shipEls = element.querySelectorAll('#bh-ships-by-day');
      if (element.dataset.phoenixOnly === 'true') {
        shipEls.forEach(function(el) { el.textContent = 'Unavailable'; });
      } else if (element.dataset.shipDayOverride) {
        var fixedDay = element.dataset.shipDayOverride;
        shipEls.forEach(function(el) { el.textContent = fixedDay; });
      } else {
        var cachedEstimate = localStorage.getItem('bhShippingEstimate');
        if (cachedEstimate) {
          var match = cachedEstimate.match(/Ships\s+(\w+)/);
          shipEls.forEach(function(el) { el.textContent = match ? match[1] : '...'; });
        } else {
          shipEls.forEach(function(el) { el.textContent = '...'; });
        }
      }

    } else if (zipType === 'upcoming') {
      // Phoenix-only products never ship, so an upcoming shipping zip can't
      // buy them — show the waitlist state instead of the launch countdown
      if (element.dataset.phoenixOnly === 'true') {
        if (cards) cards.classList.remove('visible');
        message.style.display = '';
        message.innerHTML = "Not shipping to " + savedZip + " yet. Coming soon.";
        waitlist.classList.remove('hidden');
        return;
      }

      // Western-only products won't be carried by east coast waves when they
      // open, so their countdown would promise the wrong thing — waitlist state
      if (element.dataset.westernOnly === 'true' && bhZipIsEastCoast()) {
        if (cards) cards.classList.remove('visible');
        message.style.display = '';
        message.innerHTML = "Not shipping to " + savedZip + " yet. Coming soon.";
        waitlist.classList.remove('hidden');
        return;
      }

      if (cards) cards.classList.remove('visible');
      message.style.display = 'none';
      waitlist.classList.add('hidden');

      // Center the parent description-box to align heading with countdown
      var descBox = element.closest('.description-box');
      if (descBox) descBox.style.textAlign = 'center';

      // Show countdown container
      if (countdownEl) {
        countdownEl.classList.remove('hidden');
        startLaunchCountdown(countdownEl);
      }
    } else if (zipType === 'shipping') {
      // Phoenix-only product check: NO shipping zip can buy it (not even AZ
      // ones like Tucson), so every shipping visitor sees the custom message.
      // Must run before the az-only and East Coast checks below.
      if (element.dataset.phoenixOnly === 'true') {
        if (cards) cards.classList.remove('visible');
        message.style.display = '';
        var phxTpl = element.querySelector('.bh-phoenix-only-message');
        message.innerHTML = phxTpl ? phxTpl.innerHTML : 'This product is only available for local Phoenix-area delivery.';
        waitlist.classList.add('hidden');
        return;
      }

      // AZ-only product check: non-AZ shipping customers see a custom message
      var isAzOnly = element.dataset.azOnly === 'true';
      if (isAzOnly) {
        var zipPrefix = savedZip.substring(0, 2);
        if (zipPrefix !== '85' && zipPrefix !== '86') {
          if (cards) cards.classList.remove('visible');
          message.style.display = '';
          var tpl = element.querySelector('.bh-az-only-message');
          message.innerHTML = tpl ? tpl.innerHTML : 'This product is only available for Arizona delivery.';
          waitlist.classList.add('hidden');
          return;
        }
      }
      // Western-only products ship from Azoria only, so east coast shipping
      // zips (served from Grip) see the custom message instead of the East
      // Coast arrival promise below.
      if (element.dataset.westernOnly === 'true' && bhZipIsEastCoast()) {
        if (cards) cards.classList.remove('visible');
        message.style.display = '';
        var westTpl = element.querySelector('.bh-western-only-message');
        message.innerHTML = westTpl ? westTpl.innerHTML : 'This product is only available in the western US.';
        waitlist.classList.add('hidden');
        return;
      }

      if (element.dataset.carneEcPromise && bhZipIsEastCoast()) {
        if (cards) cards.classList.remove('visible');
        message.style.display = '';
        message.textContent = element.dataset.carneEcPromise;
        var carneSnowflake = window.bhSnowflakeUrl
          ? '<img src="' + window.bhSnowflakeUrl + '" alt="" class="bh-shipping-snowflake">'
          : '';
        message.insertAdjacentHTML('beforeend', '<span class="bh-ec-dry-ice">' + carneSnowflake + 'Ships frozen on dry ice</span>');
        waitlist.classList.add('hidden');
        return;
      }

      // East Coast zips promise arrival dates instead of a ship day
      var ecHtml = bhEcMessageHtml();
      if (ecHtml) {
        if (cards) cards.classList.remove('visible');
        message.style.display = '';
        message.innerHTML = ecHtml;
        waitlist.classList.add('hidden');
        bhStartEcCountdowns(message);
        return;
      }

      // Hide cards, show single-line message
      if (cards) cards.classList.remove('visible');
      message.style.display = '';

      // Drops on a fixed shipping schedule state their own promise: the zip
      // checker is zip-scoped, so it can't answer per product and would quote
      // the normal cadence here.
      if (element.dataset.shippingPromise) {
        var fixedSnowflake = '<img src="' + (window.bhSnowflakeUrl || '') + '" alt="" class="bh-shipping-snowflake">';
        message.textContent = element.dataset.shippingPromise;
        message.insertAdjacentHTML('afterbegin', fixedSnowflake);
        waitlist.classList.add('hidden');
        return;
      }

      const estimate = localStorage.getItem('bhShippingEstimate');
      var shipsDay = '', arrivesDay = '';
      if (estimate) {
        var shipMatch = estimate.match(/Ships\s+(\w+)/);
        var arriveMatch = estimate.match(/arrives\s+(\w+)/i);
        if (shipMatch) shipsDay = shipMatch[1];
        if (arriveMatch) arrivesDay = arriveMatch[1];
      }
      var snowflake = '<img src="' + (window.bhSnowflakeUrl || '') + '" alt="" class="bh-shipping-snowflake">';
      message.innerHTML = shipsDay ? snowflake + "Ships frozen this " + shipsDay + " on dry ice. Arrives " + arrivesDay + "." : ("Ships frozen to <u>" + savedZip + "</u>");
      waitlist.classList.add('hidden');
    } else {
      // Not serviceable
      if (cards) cards.classList.remove('visible');
      message.style.display = '';
      message.innerHTML = "Not shipping to " + savedZip + " yet." + (bhIsPlannedSpring2027() ? " " + BH_SPRING_2027_LINE : "");
      waitlist.classList.remove('hidden');
    }
  });
}

function resetPostalCheckerForm() {
  const postalCheckerForms = document.querySelectorAll('.postal-checker-form');
  const postalCheckerChecked = document.querySelectorAll('.postal-checker-checked');
  const deliveryInfos = document.querySelectorAll('.bh-delivery-info');

  postalCheckerForms.forEach(function(form) { form.classList.remove('hidden'); });
  postalCheckerChecked.forEach(function(element) { element.classList.add('hidden'); });
  deliveryInfos.forEach(function(element) { element.classList.add('info-hidden'); });

  // The drawer heading carries the last zip verdict; back to the question
  // while the form is open again.
  var drawerHeading = document.querySelector('#cart-zip-check > span');
  if (drawerHeading) drawerHeading.textContent = "Check if we deliver to your area";

  // Hide delivery cards, heading zip, and discount messages
  document.querySelectorAll('.bh-delivery-cards').forEach(function(el) { el.classList.remove('visible'); });
  document.querySelectorAll('.bh-delivery-heading-zip').forEach(function(el) { el.classList.remove('visible'); });
  document.querySelectorAll('.bh-local-discount-msg').forEach(function(el) { el.classList.add('hidden'); });
  document.querySelectorAll('.bh-launch-countdown').forEach(function(el) { el.classList.add('hidden'); });
  var cartDiscountBar = document.getElementById('cart-discount-bar');
  if (cartDiscountBar) cartDiscountBar.classList.add('hidden');
  if (typeof updatePromoHeader === 'function') updatePromoHeader();
}

/* =====================================================
   Launch countdown
   ===================================================== */

function startLaunchCountdown(container) {
  var zipPhase = localStorage.getItem('bhZipPhase');
  var launchSetting = zipPhase && window.bhLaunchDates ? window.bhLaunchDates[zipPhase] : null;
  if (!launchSetting) {
    container.classList.add('hidden');
    return;
  }

  // Parse "YYYY-MM-DD HH:MM" or "YYYY-MM-DD" as Arizona time (MST = UTC-7)
  var parts = launchSetting.trim().split(/\s+/);
  var datePart = parts[0];
  var timePart = parts[1] || '00:00';
  var targetMs = new Date(datePart + 'T' + timePart + ':00-07:00').getTime();
  if (isNaN(targetMs)) {
    container.classList.add('hidden');
    return;
  }

  var headingEl = container.querySelector('.bh-countdown-heading');
  var timerEl = container.querySelector('.bh-countdown-timer');
  var savedZip = localStorage.getItem('bhZipCode');

  if (headingEl) {
    headingEl.textContent = savedZip ? "Orders open in zip " + savedZip + " soon" : "Orders open soon";
  }

  // Clear any existing interval
  if (container._countdownInterval) clearInterval(container._countdownInterval);

  function tick() {
    var now = Date.now();
    var diff = targetMs - now;

    if (diff <= 0) {
      // Launch time reached
      if (container._countdownInterval) clearInterval(container._countdownInterval);
      if (timerEl) timerEl.innerHTML = '';
      if (headingEl) headingEl.textContent = window.bhLaunchMessage || "We're live! Check your zip again.";
      return;
    }

    var days  = Math.floor(diff / 86400000);
    var hours = Math.floor((diff % 86400000) / 3600000);
    var mins  = Math.floor((diff % 3600000) / 60000);
    var secs  = Math.floor((diff % 60000) / 1000);

    if (timerEl) {
      timerEl.innerHTML =
        '<div class="bh-countdown-unit"><span class="bh-countdown-number">' + days + '</span><span class="bh-countdown-label">' + (days === 1 ? 'Day' : 'Days') + '</span></div>' +
        '<div class="bh-countdown-unit"><span class="bh-countdown-number">' + hours + '</span><span class="bh-countdown-label">' + (hours === 1 ? 'Hour' : 'Hours') + '</span></div>' +
        '<div class="bh-countdown-unit"><span class="bh-countdown-number">' + mins + '</span><span class="bh-countdown-label">' + (mins === 1 ? 'Min' : 'Mins') + '</span></div>' +
        '<div class="bh-countdown-unit"><span class="bh-countdown-number">' + secs + '</span><span class="bh-countdown-label">' + (secs === 1 ? 'Sec' : 'Secs') + '</span></div>';
    }
  }

  tick(); // Render immediately
  container._countdownInterval = setInterval(tick, 1000);
}

/* =====================================================
   Init on page load
   ===================================================== */

document.addEventListener('DOMContentLoaded', function() {
  const savedZip = localStorage.getItem('bhZipCode');
  const zipType = localStorage.getItem('bhZipType');
  updateRegionalFlavorVisibility();

  // Region scoped countdowns render hidden and are resolved here, so settle
  // them from stored state before any of the paths below can return early.
  // FAQ variants settle from the same stored state.
  updateRegionScopedCountdowns();
  updateEcFaqBlocks();

  // Migration: old visitors have bhZipCode but no bhZipType
  if (savedZip && !zipType) {
    if (window.bhZipCodes && window.bhZipCodes.whitelisted.includes(savedZip)) {
      localStorage.setItem('bhZipType', 'delivery');
    } else {
      // Need API to determine shipping eligibility
      fetchShippingStatus(savedZip)
        .then(function(result) {
          bhStorePlannedLaunch(result);
          if (result.serviceable) {
            localStorage.setItem('bhZipType', 'shipping');
            localStorage.setItem('bhShippingEstimate', result.shipping_estimate || '');
            bhStoreEcPromise(result);
          } else {
            localStorage.setItem('bhZipType', 'none');
            bhClearEcPromise();
          }
        })
        .catch(function() {
          localStorage.setItem('bhZipType', 'none');
          bhClearEcPromise();
        })
        .finally(function() {
          updatePostalChecker();
          updateAddToCartButtons();
          updateHomepageCountdown();
          updateRegionScopedCountdowns();
          updateEcFaqBlocks();
          updateDeliveryInfo();
          if (typeof updatePromoHeader === 'function') updatePromoHeader();
          updateAzOnlyCatalogVisibility();
        });
      return; // Wait for async migration
    }
  }

  // No saved zip: auto-detect via IP geolocation
  if (!savedZip) {
    var submitButtons = document.querySelectorAll('.postal-submit-btn');
    submitButtons.forEach(function(btn) { btn.disabled = true; btn.textContent = '...'; });

    fetchZipFromIP()
      .then(function(zip) { checkPostal(zip); })
      .catch(function() { checkPostal('90012'); })
      .finally(function() {
        submitButtons.forEach(function(btn) { btn.disabled = false; btn.textContent = 'CHECK'; });
      });
    return;
  }

  updatePostalChecker();
  updateAddToCartButtons();
  updateHomepageCountdown();
  updateRegionScopedCountdowns();
  updateEcFaqBlocks();
  updateDeliveryInfo();
  if (typeof updatePromoHeader === 'function') updatePromoHeader();
  updateAzOnlyCatalogVisibility();

  // Re-fetch shipping estimate for fresh data (changes based on time of day/week)
  if (savedZip && (zipType === 'shipping' || zipType === 'delivery')) {
    fetchShippingStatus(savedZip)
      .then(function(result) {
        if (result.shipping_estimate) {
          localStorage.setItem('bhShippingEstimate', result.shipping_estimate);
        }
        bhStoreEcPromise(result);
        updateAddToCartButtons();
        if (zipType === 'delivery') {
          if (result.shipping_estimate) updateShippingCard(result.shipping_estimate);
        } else {
          // Re-render even without an estimate: an east coast promise may have
          // just appeared or dropped away
          updateDeliveryInfo();
          updatePostalChecker();
          updateEcFaqBlocks();
        }
      })
      .catch(function() { /* keep existing estimate */ });
  }

  // Re-fetch for zips we can't serve yet, to detect if their phase has
  // launched. 'none' is included because east coast zips checked before their
  // wave date existed in theme settings were all stored that way, and nothing
  // else would ever upgrade them once their wave opens.
  if (savedZip && (zipType === 'upcoming' || zipType === 'none')) {
    fetchShippingStatus(savedZip)
      .then(function(result) {
        if (result.serviceable) {
          // Phase launched! Zip is now serviceable — upgrade tier
          localStorage.removeItem('bhZipPlanned');
          localStorage.setItem('bhZipType', 'shipping');
          localStorage.setItem('bhShippingEstimate', result.shipping_estimate || '');
          localStorage.removeItem('bhZipPhase');
          bhStoreEcPromise(result);
        } else if (
          zipType === 'none' &&
          result.zip_phase != null &&
          result.current_phase != null &&
          result.zip_phase > result.current_phase &&
          window.bhLaunchDates && window.bhLaunchDates[result.zip_phase]
        ) {
          // Their wave now has a date, so they move from "not yet" to "soon"
          localStorage.setItem('bhZipType', 'upcoming');
          localStorage.setItem('bhZipPhase', result.zip_phase);
          localStorage.removeItem('bhZipPlanned');
        } else {
          // Still unserved: re-render only if the planned window changed
          var plannedBefore = localStorage.getItem('bhZipPlanned');
          bhStorePlannedLaunch(result);
          if (localStorage.getItem('bhZipPlanned') === plannedBefore) return;
        }
        updatePostalChecker();
        updateAddToCartButtons();
        updateHomepageCountdown();
        updateRegionScopedCountdowns();
        updateEcFaqBlocks();
        updateDeliveryInfo();
        if (typeof updatePromoHeader === 'function') updatePromoHeader();
        updateAzOnlyCatalogVisibility();
      })
      .catch(function() { /* keep cached data */ });
  }
});

window.addEventListener('pageshow', function(event) {
  if (event.persisted) {
    updateAddToCartButtons();
    updateRegionalFlavorVisibility();
  }
});
